import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { environment } from '../../environments/environment';

import { LngLat } from 'mapbox-gl';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export enum StationType {
  gma = 'gma',
  synop = 'synop',
}

export interface StationData {
  stations: [
    {
      stationId: string;
      timeseries: {
        timeItems: [{}];
      };
    },
  ];
}

export interface StationInfo {
  stationId: number;
  name: string;
  distance?: number;
  mosId?: number;
  type?: string;
  latitude?: number;
  longitude?: number;
  icao?: string;
  observationStatus?: string;
  elevation?: number;
  gmaAlarm?: boolean;
}

export interface StationInfoRequest {
  stations: {
    stationItems: Array<StationInfo>;
  };
}

export interface StationListItem {
  stationId: number;
  overwriteStationName: string;
  elevation?: number;
}

@Injectable()
export class StationService {
  private http = inject(HttpClient);

  private urlStationSearch = environment.url.bff + '/station/search/';
  private urlSmw = environment.url.bff + '/smw/';
  private urlWebcam = environment.url.bff + '/app/webcam/';
  greenTableDesign = false;

  public getStationInfo(stationId: string): Observable<StationInfo> {
    const fullUrl = this.urlStationSearch + 'id/' + stationId;
    const sessionKey = 'stationinfo.' + stationId;
    const cachedData = this.getCacheData(sessionKey);
    if (cachedData) {
      const stationObservable = new Observable<StationInfo>(observer => {
        observer.next(cachedData);
      });
      return stationObservable;
    }
    return this.http.get<StationInfoRequest>(fullUrl).pipe(
      map(data => {
        // const data = response.json();
        if (data.stations.stationItems.length > 0) {
          for (const station of data.stations.stationItems) {
            if (station) {
              this.setCacheData(
                'stationinfo.' + stationId,
                data.stations.stationItems[0]
              );
              return station;
            }
          }
        }
        return null;
      })
    );
  }

  setCacheData(sessionKey: string, data: any): void {
    sessionStorage.setItem(sessionKey, JSON.stringify(data));
  }

  getCacheData(sessionKey: string): StationInfo {
    const cacheData = sessionStorage.getItem(sessionKey);
    if (cacheData) {
      return JSON.parse(cacheData);
    }
  }

  public findStationsAroundPosition(pos: LngLat): Observable<Array<any>> {
    return this.http
      .get<any>(
        environment.url.bffV3 +
          '/station/search/' +
          'nearest/' +
          pos.lat +
          '/' +
          pos.lng
      )
      .pipe(
        map(data => {
          return data.stations.stationItems
            .filter(station => station.stationId <= 120000)
            .filter(station => station.forecastStatus == '3')
            .slice(0, 10)
            .map(station => {
              const stationPosition = new LngLat(
                Number(station.longitude),
                Number(station.latitude)
              );

              return {
                ...station,
                position: stationPosition,
                distance: Math.round(pos.distanceTo(stationPosition) / 1000),
              };
            });
        })
      );
  }
}
